/* qty-stepper.js — KOSHO SG quantity stepper (− / readonly input / +).
 * ----------------------------------------------------------------------------
 * DOM contract:
 *   [data-qty-stepper]
 *     data-min="1" data-max="10" data-stock="10"   (max effective = min(max, stock))
 *     [data-qty-decrement]                          (button)
 *     [data-qty-input]                              (readonly input, value="1")
 *     [data-qty-increment]                          (button)
 *
 * Behaviour:
 *   - − below min, + above effectiveMax are no-ops; buttons are disabled +
 *     aria-disabled="true" when at bounds.
 *   - Input is treated as readonly; value changes only via the buttons.
 *   - Dispatches CustomEvent('koshosg:qty:change', { detail: { value } })
 *     from the root on every change.
 *
 * Exposes window.KoshoQtyStepper = { mountAll(), QtyStepper }.
 * ------------------------------------------------------------------------- */

(function (window, document) {
  'use strict';

  function QtyStepper(root) {
    if (!root || root.__koshoQtyStepper) return;
    this.root  = root;
    this.dec   = root.querySelector('[data-qty-decrement]');
    this.inc   = root.querySelector('[data-qty-increment]');
    this.input = root.querySelector('[data-qty-input]');
    this.min   = this._intAttr('data-min', 1);
    var max    = this._intAttr('data-max',   9999);
    var stock  = this._intAttr('data-stock', 9999);
    this.maxEff = Math.max(this.min, Math.min(max, stock));
    this.mounted = false;
    if (!this.input) return;
    this._bind();
    this._sync(this._readValue());
    root.__koshoQtyStepper = this;
    this.mounted = true;
  }

  QtyStepper.prototype._intAttr = function (name, fallback) {
    var raw = this.root.getAttribute(name);
    var n = parseInt(raw, 10);
    return isNaN(n) ? fallback : n;
  };

  QtyStepper.prototype._bind = function () {
    var self = this;
    if (this.dec) this.dec.addEventListener('click', function (e) {
      e.preventDefault();
      try { self.decrement(); } catch (err) {}
    });
    if (this.inc) this.inc.addEventListener('click', function (e) {
      e.preventDefault();
      try { self.increment(); } catch (err) {}
    });
  };

  QtyStepper.prototype._readValue = function () {
    var n = parseInt(this.input.value, 10);
    if (isNaN(n)) n = this.min;
    return Math.max(this.min, Math.min(n, this.maxEff));
  };

  QtyStepper.prototype.increment = function () {
    var v = this._readValue();
    if (v >= this.maxEff) return;
    this._sync(v + 1, true);
  };

  QtyStepper.prototype.decrement = function () {
    var v = this._readValue();
    if (v <= this.min) return;
    this._sync(v - 1, true);
  };

  QtyStepper.prototype._sync = function (value, emit) {
    this.input.value = String(value);
    // Sold-out / unavailable variant: the root carries [data-stepper-disabled]
    // (rendered by Liquid, toggled by comp-variant-selector.js). Force BOTH
    // buttons disabled regardless of min/max bounds so the stepper is inert.
    // maxEff is untouched, so clearing the flag restores correct bound states.
    var soldOut = this.root.getAttribute('data-stepper-disabled') !== null;
    var atMin = soldOut || value <= this.min;
    var atMax = soldOut || value >= this.maxEff;
    if (this.dec) {
      this.dec.disabled = atMin;
      this.dec.setAttribute('aria-disabled', atMin ? 'true' : 'false');
    }
    if (this.inc) {
      this.inc.disabled = atMax;
      this.inc.setAttribute('aria-disabled', atMax ? 'true' : 'false');
    }
    if (emit) {
      try {
        this.root.dispatchEvent(new CustomEvent('koshosg:qty:change', {
          bubbles: true,
          detail: { value: value }
        }));
      } catch (e) {}
    }
  };

  QtyStepper.prototype.getValue = function () { return this._readValue(); };

  /* ------------------------------------------------------------------------
   * Auto-mount
   * ------------------------------------------------------------------------ */
  function mountAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-qty-stepper]');
    var instances = [];
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__koshoQtyStepper) return;
      // Mini-cart line steppers are owned by cart.js, which posts /cart/change.js
      // on +/- click by reading [data-qty-input].value. If we also mounted here,
      // this stepper would bump input.value locally first and cart.js would then
      // read the bumped value and send +1 again — double-counting. So skip any
      // stepper inside the mini-cart drawer; cart.js is the sole owner there.
      if (root.closest('[data-mini-cart]')) return;
      try { instances.push(new QtyStepper(root)); } catch (e) {}
    });
    return instances;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }

  window.KoshoQtyStepper = { mountAll: mountAll, QtyStepper: QtyStepper };

})(window, document);
